import { MapPin, Clock, Phone, Navigation } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { WhatsAppIcon } from "./icons";
import { MAPS_URL, WHATSAPP_URL } from "./config";

const STORE_IMG =
  "https://customer-assets-lqy194kg.emergentagent.net/job_pastel-deco-gye/artifacts/484c25f7b79413df_image.png";

const INFO = [
  { icon: MapPin, label: "Dirección", value: "C.C. Plaza Quil, Local 57, Guayaquil." },
  { icon: Clock, label: "Horarios de atención", value: "Lunes a Sábado desde las 9:30 a. m." },
  { icon: Phone, label: "Teléfono / WhatsApp", value: "098 071 2611", href: WHATSAPP_URL },
];

export default function LocationSection() {
  return (
    <section id="ubicacion" className="bg-cream2 py-14 sm:py-18 lg:py-20">
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-8 lg:grid-cols-2 lg:gap-14 lg:px-12">
        <div>
          <Reveal>
            <Eyebrow>Tienda física</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-choco sm:text-4xl lg:text-5xl">
              Visítanos en Guayaquil
            </h2>
          </Reveal>

          <div className="mt-8 space-y-4">
            {INFO.map((row, i) => (
              <Reveal key={row.label} delay={0.15 + i * 0.08}>
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-rose shadow-sm">
                    <row.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-sand">
                      {row.label}
                    </p>
                    {row.href ? (
                      <a
                        data-testid="location-phone-link"
                        href={row.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-0.5 block font-display text-lg font-semibold text-choco transition hover:text-rose"
                      >
                        {row.value}
                      </a>
                    ) : (
                      <p className="mt-0.5 font-display text-lg font-semibold text-choco">
                        {row.value}
                      </p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.4} className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              data-testid="location-open-maps-btn"
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 rounded-full bg-wagreen px-7 py-3.5 font-bold text-white shadow-[0_18px_40px_-14px_rgba(37,211,102,0.65)] transition duration-300 hover:-translate-y-1"
            >
              <Navigation className="h-5 w-5" />
              Abrir Ruta en Google Maps
            </a>
            <a
              data-testid="location-whatsapp-btn"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2.5 rounded-full border-2 border-choco/15 bg-white px-7 py-3.5 font-bold text-choco transition duration-300 hover:-translate-y-1 hover:border-rose hover:text-rose"
            >
              <WhatsAppIcon className="h-5 w-5" />
              Escríbenos por WhatsApp
            </a>
          </Reveal>

          <Reveal delay={0.45} className="mt-5">
            <span
              data-testid="location-open-today-badge"
              className="inline-flex items-center gap-2 rounded-full bg-[#FFF4E5] px-4 py-2 text-xs font-bold text-[#995C00]"
            >
              <Clock className="h-3.5 w-3.5" />
              {new Date().getDay() === 0
                ? "Hoy descansamos · Te atendemos de Lunes a Sábado"
                : "Hoy abrimos desde las 9:30 a. m."}
            </span>
          </Reveal>
        </div>

        {/* Real storefront photo */}
        <Reveal delay={0.2}>
          <div className="relative" data-testid="location-photo-card">
            <div className="relative overflow-hidden rounded-[2rem] border border-linebr shadow-[0_35px_80px_-40px_rgba(44,32,28,0.45)]">
              <img
                src={STORE_IMG}
                alt="Fachada de Global Pastel, importador de insumos y utensilios de repostería, C.C. Plaza Quil Local 57, Guayaquil"
                loading="lazy"
                className="h-[320px] w-full object-cover sm:h-[420px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-choco/50 via-transparent to-transparent" />
              <span className="absolute left-5 top-5 flex items-center gap-1.5 rounded-full bg-cream/90 px-4 py-2 text-xs font-bold text-choco backdrop-blur">
                <MapPin className="h-3.5 w-3.5 text-rose" />
                Local 57
              </span>
              <span className="absolute right-5 top-5 rounded-full bg-rose px-4 py-2 font-hand text-xl font-semibold text-cream shadow-lg">
                ¡Te esperamos!
              </span>
              <div className="absolute inset-x-5 bottom-5 flex items-center justify-between gap-3 rounded-2xl bg-cream/95 px-5 py-3.5 backdrop-blur">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-sand">
                    Tienda física · Plaza Quil
                  </p>
                  <p className="truncate font-display text-sm font-semibold text-choco sm:text-base">
                    Importador de insumos y utensilios de repostería
                  </p>
                </div>
                <a
                  data-testid="location-photo-route-link"
                  href={MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Abrir ruta en Google Maps"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-wagreen text-white shadow-md transition duration-300 hover:scale-110"
                >
                  <Navigation className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
